import { Component, OnDestroy } from '@angular/core';
import { REFERRAL_COMPONENTS } from '../../../../constants/referral.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorMessages } from '../../../../config/error-messages';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { Config } from '../../../../config/config';
import { ServiceRouter } from '../../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../../constants/service.constants';
import { Environment } from '../../../../config/environment';
import { Subscription } from 'rxjs';
import { CalendarService } from '../../../../services/core/add-calender.service';
import { AnalyticsBean } from '../../../../beans/analytics/analytics-module.bean';

/**
 * Confirmed referral appointment component
 */
@Component({
	selector: 'app-confirmed-appointments',
	templateUrl: './confirmed-appointments.component.html',
	styleUrls: ['./confirmed-appointments.component.scss'],
})
export class ConfirmedAppointmentsComponent implements OnDestroy {
	/** Confirmed appointments data */
	data: any;
	/** Is current referral flag */
	isCurrentReferral: boolean = false;
	/** Referral component constants */
	referralComponents: any = REFERRAL_COMPONENTS;
	/** No data message string */
	noDataMsg: string;
	/** Data carrier subscription */
	subscriptions: Subscription[] = [];
	/** No data flag */
	noData: boolean = false;
	/** analytics data bean */
	analyticsData: AnalyticsBean;

	/**
	 * Confirmed appointments constructor
	 * @param activatedRoute contains the information about route associated
	 * @param errorMsg error messages
	 * @param spinner spinner component
	 * @param config config file that holds the service end points
	 * @param serviceRouter service router
	 * @param environment environment file that supplies env values
	 */
	constructor(
		private activatedRoute: ActivatedRoute,
		private errorMsg: ErrorMessages,
		private spinner: SpinnerComponent,
		private config: Config,
		private route: Router,
		private serviceRouter: ServiceRouter,
		private environment: Environment,
		private calendarService: CalendarService
	) {
		this.subscriptions.push(
			this.activatedRoute.params.subscribe(params => {
				try {
					this.isCurrentReferral = params.section === 'current';
				} catch (Exception) {}
				if (this.isCurrentReferral) {
					this.getConfirmedApptsForCurrentReferral();
				} else {
					this.getConfirmedApptsForPastReferral();
				}
			})
		);
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Confirmed Appointments';
		this.analyticsData.pageUrl = '/wellness/referral/current/confirmed/appointments';
	}

	/**
	 * Retrieve confirmed appointments for current referral
	 */
	getConfirmedApptsForCurrentReferral() {
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getCurrentReferrals + '?category=confirmed_appointments',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.confirmedAppointments;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.confirmedAppointments;
					}
				)
		);
	}

	/**
	 * Retrieve confirmed appointments for past referral
	 */
	getConfirmedApptsForPastReferral() {
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getPastReferrals + '?category=confirmed_appointments',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.confirmedAppointments;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.confirmedAppointments;
					}
				)
		);
	}

	/**
	 * Sends an email message to referral team
	 */
	sendMessage() {
		this.analyticsData.id = 'Send Message to referral team';
		this.analyticsData.redirectedTo = '/messaging/compose';
		this.analyticsData.placement = 'Secure messaging system';
		this.route.navigate(['/messaging/compose'], {
			queryParams: {
				params: btoa(JSON.stringify({ fromConfirmedAppointmentReferral: true })),
			},
		});
	}

	/**
	 * Print confirmed appointment details
	 */
	print() {
		window.print();
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
	/**
	 * Generates a calendar event
	 * @param appointment Appointment details
	 */
	generateCalendarEvent(appointment: any) {
		const dateArray = appointment.appt.apptDateTime.split(' ');
		const date = new Date(
			`${dateArray[1]} ${dateArray[2]} ${dateArray[3]} ${dateArray[5]} ${dateArray[6]} ${dateArray[7]}`
		);
		this.calendarService.addToCalendar(
			appointment.id,
			appointment.name + ' - Referral Appointment',
			'',
			`${appointment.name}, ${appointment.city}, ${appointment.state}, ${appointment.zip}`,
			date,
			''
		);
	}
}
